import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  DialogActions,
  Button,
  Divider,
  Grid2,
  Stack,
  Card,
  Chip,
  LinearProgress,
  Avatar,
  Tab,
  Tabs,
  Paper,
} from '@mui/material';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { DateTime } from 'luxon';
import API from '../../api/api';
import { useDispatch } from 'react-redux';
import { setSnackError } from '../../Reducers/snackErrors';

// ─── Shared table head cell ───────────────────────────────────────────────────
function TH({ children, align }) {
  return (
    <TableCell align={align} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em' }}>
        {children}
      </Typography>
    </TableCell>
  );
}

// ─── Detail row inside right panel ───────────────────────────────────────────
function DetailItem({ label, value, color = 'text.primary' }) {
  return (
    <Box>
      <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" color={color} fontWeight={500}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ icon: Icon, message }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, opacity: 0.35, p: 4 }}>
      <Icon sx={{ fontSize: 40 }} />
      <Typography variant="body2" textAlign="center">{message}</Typography>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const StudentInfoSession = ({ sessionInfo, assessmentInfo, open, close }) => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);

  const closeDialog = () => {
    setSelectedSession(null);
    setSelectedAssessment(null);
    setTab(0);
    close();
  };

  const handleSessionClick = (row) => {
    setSelectedAssessment(null);
    setSelectedSession(row);
  };

  const handleAssessmentClick = async (row) => {
    setSelectedSession(null);
    setLoadingAssessment(true);
    try {
      const response = await API.get('/student_assessment_search', {
        params: { student_assessment_id: row.id, easy_score: false },
      });
      if (response.data.data) setSelectedAssessment(response.data.data);
    } catch {
      dispatch(setSnackError({ title: 'Error', body: 'Unable to fetch assessment details.' }));
    } finally {
      setLoadingAssessment(false);
    }
  };

  const handleTabChange = (_, newVal) => {
    setTab(newVal);
    setSelectedSession(null);
    setSelectedAssessment(null);
  };

  const hasDetailOpen = selectedSession || selectedAssessment || loadingAssessment;

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="lg" scroll="paper">

      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>Activity logs</Typography>
        <Typography variant="body2" color="text.secondary">
          View past sessions and assessment results
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, height: '72vh', overflow: 'hidden' }}>
        <Grid2 container sx={{ height: '100%' }}>

          {/* ── Left: tabbed tables ── */}
          <Grid2
            size={{ xs: 12, md: hasDetailOpen ? 5 : 12 }}
            sx={{
              borderRight: hasDetailOpen ? '1px solid' : 'none',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              transition: 'all 0.2s ease',
            }}
          >
            {/* Tabs */}
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 44,
                  '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' },
                }}
              >
                <Tab
                  icon={<EventNoteIcon sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                  label={`Sessions${sessionInfo?.length ? ` (${sessionInfo.length})` : ''}`}
                />
                <Tab
                  icon={<AssessmentIcon sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                  label={`Assessments${assessmentInfo?.length ? ` (${assessmentInfo.length})` : ''}`}
                />
              </Tabs>
            </Box>

            {/* Table area */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>

              {/* Sessions tab */}
              {tab === 0 && (
                sessionInfo?.length ? (
                  <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TH>Date</TH>
                            <TH>Tutor</TH>
                            <TH>Program</TH>
                            <TH align="right">Time</TH>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sessionInfo.map((row) => (
                            <TableRow
                              key={row.id}
                              hover
                              selected={selectedSession?.id === row.id}
                              onClick={() => handleSessionClick(row)}
                              sx={{
                                cursor: 'pointer',
                                '&.Mui-selected': { bgcolor: 'primary.50' },
                                '&.Mui-selected:hover': { bgcolor: 'primary.50' },
                              }}
                            >
                              <TableCell sx={{ py: 0.75, borderColor: 'divider', whiteSpace: 'nowrap' }}>
                                <Typography variant="caption" fontWeight={500}>
                                  {DateTime.fromISO(row.session_date, { zone: 'utc' }).toFormat('MMM dd, yyyy')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.75, borderColor: 'divider' }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Typography variant="body2">{row.first_name} {row.last_name}</Typography>
                                  {row.substitute && (
                                    <Chip
                                      size="small"
                                      icon={<PublishedWithChangesIcon />}
                                      label="Sub"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.62rem', borderRadius: 1 }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ py: 0.75, borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary">{row.program_name}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.75, borderColor: 'divider' }}>
                                <Typography variant="caption">
                                  {DateTime.fromFormat(row.start_time, 'HH:mm:ss').toFormat('h:mm a')}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ) : (
                  <EmptyState icon={EventNoteIcon} message="No sessions recorded" />
                )
              )}

              {/* Assessments tab */}
              {tab === 1 && (
                assessmentInfo?.length ? (
                  <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TH>Date</TH>
                            <TH>Assessment</TH>
                            <TH align="right">Score</TH>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assessmentInfo.map((row) => {
                            const pct = Math.round((row.score / row.max_score) * 100);
                            const isSelected = selectedAssessment?.[0]?.student_assessment_id === row.id;
                            return (
                              <TableRow
                                key={row.id}
                                hover
                                selected={isSelected}
                                onClick={() => handleAssessmentClick(row)}
                                sx={{
                                  cursor: 'pointer',
                                  '&.Mui-selected': { bgcolor: 'secondary.50' },
                                  '&.Mui-selected:hover': { bgcolor: 'secondary.50' },
                                }}
                              >
                                <TableCell sx={{ py: 0.75, borderColor: 'divider', whiteSpace: 'nowrap' }}>
                                  <Typography variant="caption" fontWeight={500}>
                                    {DateTime.fromISO(row.session_date).toFormat('MMM dd, yyyy')}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, borderColor: 'divider' }}>
                                  <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.75, borderColor: 'divider', minWidth: 120 }}>
                                  <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      color={pct >= 70 ? 'success.main' : pct >= 50 ? 'warning.main' : 'error.main'}
                                    >
                                      {pct}%
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{ width: 60, height: 4, borderRadius: 1 }}
                                      color={pct >= 70 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ) : (
                  <EmptyState icon={AssessmentIcon} message="No assessments recorded" />
                )
              )}
            </Box>
          </Grid2>

          {/* ── Right: detail panel ── */}
          {hasDetailOpen && (
            <Grid2
              size={{ xs: 12, md: 7 }}
              sx={{ overflowY: 'auto', height: '100%' }}
            >
              {loadingAssessment ? (
                <Box sx={{ p: 3 }}>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    Loading assessment…
                  </Typography>
                </Box>

              ) : selectedSession ? (
                <Box sx={{ p: 3 }}>
                  {/* Session detail header */}
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                      <HistoryEduIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>Session details</Typography>
                      <Typography variant="caption" color="text.disabled">#{selectedSession.id}</Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                      <Chip
                        size="small"
                        icon={selectedSession.absent ? <CancelOutlinedIcon /> : <CheckCircleOutlineIcon />}
                        label={selectedSession.absent ? 'Absent' : 'Present'}
                        color={selectedSession.absent ? 'error' : 'success'}
                        variant="outlined"
                        sx={{ borderRadius: 1, fontWeight: 600 }}
                      />
                    </Box>
                  </Stack>

                  {/* Stats grid */}
                  <Grid2 container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { label: 'Program', value: selectedSession.program_name },
                      { label: 'Duration', value: `${selectedSession.session_duration} mins` },
                      { label: 'Class size', value: `${selectedSession.student_count} students` },
                      { label: 'Date', value: DateTime.fromISO(selectedSession.session_date, { zone: 'utc' }).toFormat('MMM dd, yyyy') },
                    ].map((item) => (
                      <Grid2 key={item.label} size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                          <DetailItem label={item.label} value={item.value} />
                        </Paper>
                      </Grid2>
                    ))}
                  </Grid2>

                  <Divider sx={{ mb: 2 }} />

                  {/* Notes */}
                  <Typography
                    variant="overline"
                    sx={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: 'text.disabled', fontWeight: 700 }}
                  >
                    Instructor notes
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontStyle: selectedSession.notes ? 'normal' : 'italic', color: selectedSession.notes ? 'text.primary' : 'text.disabled', lineHeight: 1.7 }}
                  >
                    {selectedSession.notes || 'No notes recorded for this session.'}
                  </Typography>
                </Box>

              ) : selectedAssessment ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Assessment breakdown</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {selectedAssessment.length} question{selectedAssessment.length !== 1 ? 's' : ''}
                  </Typography>

                  <Stack spacing={1.5}>
                   
                    {selectedAssessment.map((item, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        {/* Question */}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="primary" fontWeight={700} sx={{ letterSpacing: '0.06em' }}>
                              Q{index + 1}
                            </Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
                              {item.question}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${item.points} pts`}
                            size="small"
                            color={item.is_correct ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ borderRadius: 1, fontWeight: 700, flexShrink: 0 }}
                          />
                        </Stack>

                        <Divider sx={{ my: 1 }} />

                        {/* Answer */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {item.is_correct
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            : <CancelOutlinedIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          }
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={item.is_correct ? 'success.main' : 'error.main'}
                          >
                            {item.answer_text || item.choice_text}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Grid2>
          )}

          {/* ── No selection placeholder (full width) ── */}
          {!hasDetailOpen && (
            <Box sx={{ display: 'none' }} /> // handled by table taking full width
          )}
        </Grid2>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={closeDialog}
          sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentInfoSession;